/* eslint-disable max-lines */
import { ChevronLeftIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import { LocationZoneSelector } from "../../components/esim/LocationZoneSelector";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/input/Input";
import SpinnerLoader from "../../components/ui/SpinnerLoader";
import { useDebounce } from "../../hooks/useDebounce";
import useFetch from "../../hooks/useFetch";
import {
  IEsimPackage,
  ILocationZone
} from "../../interfaces/esim/EsimPackages.types";
import {
  getLocationZones,
  getPackagesByLocationZone
} from "../../services/Esim.service";
import {
  getEsimPartnerById,
  topUpEsimPartnerAllocation
} from "../../services/EsimPartner.service";

const formatPackage = (pkg: IEsimPackage) => {
  const size = pkg.isUnlimited ? "Unlimited" : `${pkg.totalGB} GB`;
  const validity = pkg.validityDays ? `${pkg.validityDays} ditë` : "";
  return [size, validity].filter(Boolean).join(" • ");
};

const PartnerAllocationsPage: React.FC = () => {
  const { id } = useParams();
  const partnerId = Number(id);

  const { response, setResponse, loading } = useFetch(getEsimPartnerById, {
    initRequest: true,
    params: partnerId
  });

  // Zone autocomplete
  const [zoneSearch, setZoneSearch] = useState("");
  const [zones, setZones] = useState<ILocationZone[]>([]);
  const [selectedZone, setSelectedZone] = useState<ILocationZone | null>(null);
  const [zoneInputFocused, setZoneInputFocused] = useState(false);
  const debouncedZoneSearch = useDebounce(zoneSearch, 300);

  // Packages for the picked zone
  const [packages, setPackages] = useState<IEsimPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | "">("");

  // Top-up state
  const [addCount, setAddCount] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);

  // Load zones for the autocomplete
  useEffect(() => {
    const run = async () => {
      try {
        const res = await getLocationZones(debouncedZoneSearch);
        if (res.data.succeeded) setZones(res.data.data);
      } catch {
        toast.error("Deshtoi marrja e zonave");
      }
    };
    void run();
  }, [debouncedZoneSearch]);

  // When a zone is picked, fetch its packages
  useEffect(() => {
    const run = async () => {
      if (!selectedZone) {
        setPackages([]);
        setSelectedPackageId("");
        return;
      }
      setPackagesLoading(true);
      try {
        const res = await getPackagesByLocationZone(selectedZone.slug);
        setPackages(res.data || []);
      } catch {
        toast.error("Deshtoi marrja e paketave per kete zone");
        setPackages([]);
      } finally {
        setPackagesLoading(false);
      }
    };
    void run();
  }, [selectedZone]);

  const partner = response?.data;
  const selectedPackage = packages.find((p) => p.id === selectedPackageId);

  const resetPicker = () => {
    setSelectedZone(null);
    setZoneSearch("");
    setPackages([]);
    setSelectedPackageId("");
    setAddCount(1);
  };

  const handleTopUp = async () => {
    if (!selectedPackageId) {
      toast.error("Zgjidh nje pakete");
      return;
    }
    if (!addCount || addCount <= 0) {
      toast.error("Vendos nje sasi me te madhe se zero");
      return;
    }
    setSubmitting(true);
    try {
      const res = await topUpEsimPartnerAllocation(partnerId, {
        packageTemplateId: Number(selectedPackageId),
        addCount
      });
      if (res.data) {
        setResponse((prev) =>
          prev ? { ...prev, data: res.data } : ({ ...res, data: res.data } as any)
        );
        toast.success(`Bilanci u rrit me ${addCount}`);
        resetPicker();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Deshtoi rritja e bilancit"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !partner) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/esim-partners"
        state={["Partneret eSIM"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>

      {/* Partner header */}
      <div className="mb-6 flex items-center gap-3">
        {partner.logoUrl && (
          <img
            src={partner.logoUrl}
            alt={partner.companyName}
            className="h-10 w-10 rounded object-contain bg-gray-50"
          />
        )}
        <div>
          <h1 className="text-lg font-semibold">{partner.companyName}</h1>
          <p className="text-xs text-gray-500">{partner.allowedEmail}</p>
        </div>
      </div>

      {/* Top-up picker */}
      <div className="mb-8 rounded-lg border border-gray-200 p-4">
        <p className="text-sm font-semibold text-gray-800 mb-3">
          Shto bilanc per nje pakete
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          {/* Step 1: Zone */}
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              1. Zona
            </label>
            <LocationZoneSelector
              search={zoneSearch}
              setSearch={setZoneSearch}
              selectedZone={selectedZone}
              locationZones={zones}
              isInputFocused={zoneInputFocused}
              setIsInputFocused={setZoneInputFocused}
              onZoneSelect={(z) => {
                setSelectedZone(z);
                setSelectedPackageId("");
              }}
              onZoneClear={resetPicker}
            />
          </div>

          {/* Step 2: Package */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              2. Paketa
            </label>
            <select
              value={selectedPackageId}
              onChange={(e) =>
                setSelectedPackageId(
                  e.target.value ? Number(e.target.value) : ""
                )
              }
              disabled={!selectedZone || packagesLoading}
              className={
                "w-full p-2 border border-gray-300 rounded-lg " +
                "disabled:bg-gray-100 disabled:cursor-not-allowed"
              }
            >
              <option value="">
                {!selectedZone
                  ? "Zgjedh zonen fillimisht"
                  : packagesLoading
                    ? "Po ngarkohen paketat..."
                    : packages.length === 0
                      ? "Pa pakete ne kete zone"
                      : "Zgjedh paketen"}
              </option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {formatPackage(p)}
                </option>
              ))}
            </select>
          </div>

          {/* Step 3: Count */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              3. Sasia
            </label>
            <Input
              type="number"
              min={1}
              value={addCount || ""}
              onChange={(e: any) => setAddCount(Number(e.target.value))}
            />
          </div>
        </div>

        {selectedPackage && (
          <p className="mt-3 text-xs text-gray-500">
            Po shton <strong>{addCount || 0}</strong> eSIM për{" "}
            <strong>
              {selectedZone?.name} {formatPackage(selectedPackage)}
            </strong>
            .
          </p>
        )}

        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            onClick={handleTopUp}
            disabled={submitting || !selectedPackageId || addCount <= 0}
            className="flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            {submitting ? "Po ruhet..." : "Shto ne bilanc"}
          </Button>
        </div>
      </div>

      {/* Current balances — read-only list */}
      <div>
        <p className="text-sm font-semibold text-gray-800 mb-3">
          Bilanci aktual
        </p>
        {(partner.allocations || []).length === 0 ? (
          <p
            className={
              "rounded-lg border border-dashed border-gray-200 " +
              "p-6 text-center text-sm text-gray-500"
            }
          >
            Ende nuk ka pakete te alokuara per kete partner.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {partner.allocations.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {a.packageName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {a.isUnlimited
                      ? "Unlimited"
                      : a.dataGb !== null && a.dataGb !== undefined
                        ? `${a.dataGb.toFixed(a.dataGb < 1 ? 2 : 0)} GB`
                        : "—"}
                    {a.validityDays ? ` • ${a.validityDays} ditë` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold tabular-nums text-primary">
                    {a.balanceRemaining}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">
                    të mbetura
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PartnerAllocationsPage;
